import React, { useState, useEffect } from 'react';
import { Pesée } from '@/types';
import weighingService from '@/services/weighing';
import { useWeighing } from '@/contexts/WeighingContext';
import { resolveWeighingProcess, getEffectivePeseeStatut, getInProgressTargetStep } from '@/utils/weighingProcess';

interface PendingWeighingsListProps {
  onWeighingSelect: (weighing: Pesée) => void;
  className?: string;
  onRefresh?: () => void; // Callback pour notifier le parent du rafraîchissement
  loadedWeighingId?: string; // ID de la pesée actuellement chargée
}

const PendingWeighingsList: React.FC<PendingWeighingsListProps> = ({
  onWeighingSelect,
  className = '',
  onRefresh,
  loadedWeighingId
}) => {
  const { setCurrentWeighing } = useWeighing();
  const [pendingWeighings, setPendingWeighings] = useState<Pesée[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(3); // Afficher seulement 3 éléments

  // Charger les pesées en cours (non terminées)
  const loadPendingWeighings = async () => {
    try {
      setLoading(true);
      
      // Toujours utiliser getPesées et filtrer côté client pour garantir la cohérence
      // avec le compteur qui utilise la même logique
      const allWeighings = await weighingService.getPesées({
        page_size: 1000 // Récupérer un grand nombre pour avoir toutes les pesées
      });
      
      if (allWeighings.results) {
        // Filtrer les pesées non terminées avec la même logique que loadPendingWeighingsCount
        const pendingWeighings = allWeighings.results.filter((weighing: Pesée) => {
          // Exclure les pesées terminées (statut COMPLETEE, ACHEVEE, REFOULEE, ANNULEE)
          if (!weighing.statut || ['COMPLETEE', 'ACHEVEE', 'REFOULEE', 'ANNULEE'].includes(weighing.statut)) {
            return false;
          }
          
          // Exclure les pesées qui ont les deux poids enregistrés (pesée complète)
          // Peu importe le processus, si les deux poids sont présents, la pesée est complète
          const hasBothWeights = weighing.poids_brut && weighing.poids_tare;
          if (hasBothWeights) {
            return false; // Pesée complète, ne pas afficher dans les pesées en attente
          }
          
          return true;
        });
        
        setPendingWeighings(pendingWeighings);
      } else {
        setPendingWeighings([]);
      }
    } catch (error) {
      console.error('❌ Erreur lors du chargement des pesées en cours:', error);
      setPendingWeighings([]);
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour rafraîchir la liste
  const handleRefresh = async () => {
    await loadPendingWeighings();
    if (onRefresh) {
      onRefresh();
    }
  };

  // Charger les données au montage du composant
  useEffect(() => {
    loadPendingWeighings();
  }, []);

  // Filtrer les pesées selon le terme de recherche
  const filteredWeighings = pendingWeighings.filter(weighing => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    
    // Vérifier la référence
    if (weighing.reference?.toLowerCase().includes(searchLower)) return true;
    
    // Vérifier le camion (peut être un objet ou une string)
    if (typeof weighing.camion === 'string' && weighing.camion.toLowerCase().includes(searchLower)) return true;
    if (typeof weighing.camion === 'object' && weighing.camion && 'immatriculation' in weighing.camion && (weighing.camion as any).immatriculation?.toLowerCase().includes(searchLower)) return true;
    
    // Vérifier le produit (peut être un objet ou une string)
    if (typeof weighing.produit === 'string' && weighing.produit.toLowerCase().includes(searchLower)) return true;
    if (typeof weighing.produit === 'object' && weighing.produit && 'nom' in weighing.produit && (weighing.produit as any).nom?.toLowerCase().includes(searchLower)) return true;
    
    // Vérifier d'autres champs possibles
    if (weighing.camion_immatriculation?.toLowerCase().includes(searchLower)) return true;
    if (weighing.produit_nom?.toLowerCase().includes(searchLower)) return true;
    if (weighing.chauffeur_nom?.toLowerCase().includes(searchLower)) return true;
    
    return false;
  });

  // Calculer la pagination
  const totalPages = Math.ceil(filteredWeighings.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentWeighings = filteredWeighings.slice(startIndex, endIndex);

  
  const handleLoadWeighing = (weighing: Pesée) => {
    setCurrentWeighing(weighing);

    const processus = resolveWeighingProcess(weighing);
    const targetStep = getInProgressTargetStep(weighing);

    const weighingWithStep = {
      ...weighing,
      processus,
      targetStep
    };

    onWeighingSelect(weighingWithStep);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1); // Retour à la première page lors de la recherche
  };

  // Fonction pour calculer le pourcentage de progression
  const getProgressPercentage = (weighing: Pesée): number => {
    const isInverted = resolveWeighingProcess(weighing) === 'inverted';
    let progress = 0;
    
    // Étape 1: Informations de base (20%)
    if (weighing.camion && weighing.produit) progress += 20;
    
    if (isInverted) {
      // Mode inversé : étape 1 = tare, étape 3 = brut
      // Étape 2: Poids tare (20%)
      if (weighing.poids_tare) progress += 20;
      
      // Étape 3: Analyse qualité (20%)
      if (weighing.observations && weighing.observations.length > 10) progress += 20;
      
      // Étape 4: Poids brut (20%)
      if (weighing.poids_brut) progress += 20;
    } else {
      // Mode standard : étape 1 = brut, étape 3 = tare
      // Étape 2: Poids brut (20%)
      if (weighing.poids_brut) progress += 20;
      
      // Étape 3: Analyse qualité (20%)
      if (weighing.observations && weighing.observations.length > 10) progress += 20;
      
      // Étape 4: Poids tare (20%)
      if (weighing.poids_tare) progress += 20;
    }
    
    // Étape 5: Finalisation (20%)
    if (weighing.statut === 'COMPLETEE') progress += 20;
    
    return Math.min(progress, 100);
  };

  // Fonction pour obtenir le texte de progression
  const getProgressText = (weighing: Pesée): string => {
    const isInverted = resolveWeighingProcess(weighing) === 'inverted';
    
    if (weighing.statut === 'COMPLETEE') return 'Pesée complète - Prêt pour archivage';
    
    if (isInverted) {
      // Mode inversé
      if (weighing.poids_tare && weighing.poids_brut) return 'Pesée complète - Prêt pour finalisation';
      if (weighing.poids_tare && !weighing.poids_brut) return 'Poids tare enregistré - En attente de pesée brut';
      if (weighing.observations && weighing.observations.length > 10) return 'Analyse qualité terminée - En attente de pesée brut';
      if (weighing.camion && weighing.produit) return 'Informations de base saisies - En attente de pesée tare';
      return 'Nouvelle pesée (mode inversé) - Informations de base à saisir';
    } else {
      // Mode standard
      if (weighing.poids_tare) return 'Pesée tare terminée - En attente de finalisation';
      if (weighing.observations && weighing.observations.length > 10) return 'Analyse qualité terminée - En attente de pesée tare';
      if (weighing.poids_brut) return 'Poids brut enregistré - En attente d\'analyse qualité';
      if (weighing.camion && weighing.produit) return 'Informations de base saisies - En attente de pesée brut';
      return 'Nouvelle pesée - Informations de base à saisir';
    }
  };

  if (loading) {
    return (
      <div className={`bg-white rounded-xl shadow-sm p-6 ${className}`}>
        <div className="flex items-center justify-center p-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-sm text-gray-600">Chargement...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-xl shadow-sm p-6 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-medium text-gray-900">
          Pesées en cours ({pendingWeighings.length})
        </h3>
        <div className="flex items-center space-x-2">
          <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium">
            {filteredWeighings.length} trouvée(s)
          </span>
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
            title="Rafraîchir la liste"
          >
            <svg className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>
      </div>

      {/* Barre de recherche */}
      <div className="mb-4">
        <input
          type="text"
          placeholder="Rechercher par référence, véhicule ou produit..."
          value={searchTerm}
          onChange={(e) => handleSearchChange(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
        />
      </div>

      {/* Liste des pesées avec scroll */}
      {currentWeighings.length === 0 ? (
        <div className="text-center text-gray-500 py-6">
          {searchTerm ? 'Aucune pesée trouvée' : 'Aucune pesée en cours'}
          <div className="text-xs text-gray-400 mt-2">
            Debug: {loading ? 'Chargement...' : `Total: ${pendingWeighings.length}, Filtrées: ${filteredWeighings.length}`}
          </div>
        </div>
      ) : (
        <div className="relative">
          {filteredWeighings.length > 3 && (
            <>
              <div className="absolute top-0 right-0 z-10 bg-gradient-to-l from-white to-transparent w-8 h-full pointer-events-none"></div>
              <div className="absolute bottom-0 right-0 z-10 bg-gradient-to-t from-white to-transparent w-8 h-full pointer-events-none"></div>
            </>
          )}
          <div className="max-h-96 overflow-y-auto space-y-3 mb-4 pr-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 hover:scrollbar-thumb-gray-400">
          {currentWeighings.map((weighing) => (
            <div
              key={weighing.id}
              className={`border rounded-lg p-3 transition-colors group ${
                loadedWeighingId === weighing.id 
                  ? 'border-blue-300 bg-blue-50' 
                  : 'border-gray-200 hover:bg-gray-50'
              }`}
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="font-medium text-gray-900 text-sm">
                    {weighing.reference || 'Réf. N/A'}
                  </div>
                  <div className="text-xs text-gray-600 mt-1">
                    {weighing.camion && `Véhicule: ${weighing.camion}`}
                  </div>
                  <div className="text-xs text-gray-600">
                    {weighing.produit && `Produit: ${weighing.produit}`}
                  </div>
                  {weighing.poids_brut && (
                    <div className="text-xs text-gray-600">
                      Poids brut: {weighing.poids_brut} kg
                    </div>
                  )}
                  {weighing.poids_tare && (
                    <div className="text-xs text-gray-600">
                      Poids tare: {weighing.poids_tare} kg
                    </div>
                  )}
                  {weighing.observations && (
                    <div className="text-xs text-gray-500 mt-1 truncate">
                      {weighing.observations}
                    </div>
                  )}
                </div>
                <div className="text-right">
                  <div className="text-xs text-gray-500">
                    {weighing.created_at && new Date(weighing.created_at).toLocaleDateString('fr-FR')}
                  </div>
                  <div className="mt-1 flex flex-col items-end gap-1">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      getEffectivePeseeStatut(weighing) === 'EN_COURS' ? 'bg-blue-100 text-blue-800' :
                      getEffectivePeseeStatut(weighing) === 'EN_ATTENTE_TARE' ? 'bg-yellow-100 text-yellow-800' :
                      getEffectivePeseeStatut(weighing) === 'BRUT_ENREGISTRE' ? 'bg-green-100 text-green-800' :
                      getEffectivePeseeStatut(weighing) === 'TARE_ENREGISTREE' ? 'bg-purple-100 text-purple-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {(() => {
                        const effective = getEffectivePeseeStatut(weighing);
                        const isInverted = resolveWeighingProcess(weighing) === 'inverted';

                        if (effective === 'EN_COURS') return 'En cours';
                        if (effective === 'EN_ATTENTE_TARE') {
                          return isInverted ? 'En attente brut' : 'En attente tare';
                        }
                        if (effective === 'BRUT_ENREGISTRE') return 'Brut enregistré';
                        if (effective === 'TARE_ENREGISTREE') {
                          return isInverted ? 'Tare enregistrée — attente brut' : 'Tare enregistrée';
                        }
                        return weighing.statut;
                      })()}
                    </span>
                    <span className="text-xs text-gray-400">
                      {resolveWeighingProcess(weighing) === 'inverted' ? 'Mode inversé' : 'Mode standard'}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Barre de progression */}
              <div className="mt-3">
                <div className="flex justify-between items-center text-xs text-gray-600 mb-1">
                  <span>Progression</span>
                  <span>{getProgressPercentage(weighing)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                    style={{ width: `${getProgressPercentage(weighing)}%` }}
                  ></div>
                </div>
              </div>
              
              {/* Bouton pour charger la pesée */}
              <div className="mt-3 flex justify-between items-center">
                <div className="text-xs text-gray-500">
                  {getProgressText(weighing)}
                </div>
                {loadedWeighingId === weighing.id ? (
                  <div className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-100 rounded-md">
                    <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Chargée
                  </div>
                ) : (
                  <button
                    onClick={() => handleLoadWeighing(weighing)}
                    className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                    </svg>
                    Continuer
                  </button>
                )}
              </div>
            </div>
          ))}
          </div>
          {filteredWeighings.length > 3 && (
            <div className="text-center text-xs text-gray-400 mt-2">
              <svg className="w-4 h-4 mx-auto animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
              <span className="block mt-1">Défilez pour voir plus</span>
            </div>
          )}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="border-t border-gray-200 pt-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="text-xs sm:text-sm text-gray-600 order-2 sm:order-1">
              Affichage {startIndex + 1}-{Math.min(endIndex, filteredWeighings.length)} sur {filteredWeighings.length}
            </div>
            <div className="flex items-center space-x-1 order-1 sm:order-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-2 py-1 text-xs sm:text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                Précédent
              </button>
              
              {/* Pagination avec ellipses pour les grandes listes */}
              <div className="flex items-center space-x-1">
                {(() => {
                  const pages = [];
                  const maxVisiblePages = 5;
                  
                  if (totalPages <= maxVisiblePages) {
                    // Afficher toutes les pages si peu de pages
                    for (let i = 1; i <= totalPages; i++) {
                      pages.push(
                        <button
                          key={i}
                          onClick={() => handlePageChange(i)}
                          className={`px-2 py-1 text-xs sm:text-sm border rounded ${
                            currentPage === i
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {i}
                        </button>
                      );
                    }
                  } else {
                    // Logique avec ellipses
                    const startPage = Math.max(1, currentPage - 2);
                    const endPage = Math.min(totalPages, currentPage + 2);
                    
                    // Première page
                    if (startPage > 1) {
                      pages.push(
                        <button
                          key={1}
                          onClick={() => handlePageChange(1)}
                          className="px-2 py-1 text-xs sm:text-sm border border-gray-300 rounded hover:bg-gray-50"
                        >
                          1
                        </button>
                      );
                      if (startPage > 2) {
                        pages.push(<span key="ellipsis1" className="px-1 text-gray-500">...</span>);
                      }
                    }
                    
                    // Pages autour de la page courante
                    for (let i = startPage; i <= endPage; i++) {
                      pages.push(
                        <button
                          key={i}
                          onClick={() => handlePageChange(i)}
                          className={`px-2 py-1 text-xs sm:text-sm border rounded ${
                            currentPage === i
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {i}
                        </button>
                      );
                    }
                    
                    // Dernière page
                    if (endPage < totalPages) {
                      if (endPage < totalPages - 1) {
                        pages.push(<span key="ellipsis2" className="px-1 text-gray-500">...</span>);
                      }
                      pages.push(
                        <button
                          key={totalPages}
                          onClick={() => handlePageChange(totalPages)}
                          className="px-2 py-1 text-xs sm:text-sm border border-gray-300 rounded hover:bg-gray-50"
                        >
                          {totalPages}
                        </button>
                      );
                    }
                  }
                  
                  return pages;
                })()}
              </div>
              
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-2 py-1 text-xs sm:text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                Suivant
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingWeighingsList;
